import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Users, Gamepad2, Plus, X, Loader2, Search, ChevronDown, ChevronUp, Dice5, Spade, Dices, Layers, Grid3X3, Settings2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import api from "../lib/api";

const CATEGORY_ICONS = {
  okey: Dice5,
  batak: Spade,
  tavla: Dices,
  iskambil: Layers,
  strateji: Grid3X3,
  sosyal: Users
};

export default function CreateLeague() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedGame = searchParams.get("game");

  const [loading, setLoading] = useState(false);
  const [games, setGames] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [selectedGame, setSelectedGame] = useState(preselectedGame || "");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [playerUsernames, setPlayerUsernames] = useState([""]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [customRules, setCustomRules] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [gamesRes, catsRes] = await Promise.all([
          api.getGames(),
          api.getGameCategories()
        ]);
        setGames(gamesRes.data);
        setCategories(catsRes.data);
        
        // Auto-expand first category
        if (catsRes.data.length > 0) {
          setExpandedCategory(catsRes.data[0].id);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await api.searchUsers(query);
      setSearchResults(response.data);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setSearching(false);
    }
  };

  const addPlayer = (username) => {
    if (!playerUsernames.includes(username)) {
      setPlayerUsernames([...playerUsernames.filter(u => u), username]);
    }
    setSearchQuery("");
    setSearchResults([]);
  };

  const removePlayer = (index) => {
    setPlayerUsernames(playerUsernames.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedGame) {
      toast.error("Lütfen bir oyun seçin");
      return;
    }
    
    const validUsernames = playerUsernames.filter(u => u.trim());
    if (validUsernames.length < 2) {
      toast.error("En az 2 oyuncu gerekli");
      return;
    }

    setLoading(true);
    try {
      const response = await api.createLeague({
        name,
        game_id: selectedGame,
        description,
        player_usernames: validUsernames,
        is_private: false,
        custom_rules: customRules,
      });
      
      toast.success("Lig oluşturuldu!");
      navigate(`/league/${response.data.league_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Lig oluşturulamadı");
    } finally {
      setLoading(false);
    }
  };

  const selectedGameData = games.find(g => g.game_id === selectedGame);
  
  // Group games by category
  const gamesByCategory = categories.reduce((acc, cat) => {
    acc[cat.id] = games.filter(g => g.category === cat.id);
    return acc;
  }, {});

  const handleRuleChange = (ruleName, value) => {
    setCustomRules(prev => ({ ...prev, [ruleName]: value }));
  };

  const toggleCategory = (catId) => {
    setExpandedCategory(expandedCategory === catId ? null : catId);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="create-league-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
          Yeni Lig Oluştur
        </h1>
        <p className="text-[#B1C1C1] mt-1">Arkadaşlarınla yeni bir lig başlat</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Game Selection by Category */}
        <div className="glass-card p-6">
          <Label className="text-[#C6A87C] text-sm uppercase tracking-wider">Oyun Seç</Label>
          
          <div className="mt-4 space-y-3">
            {categories.map((category) => {
              const CategoryIcon = CATEGORY_ICONS[category.id] || Gamepad2;
              const categoryGames = gamesByCategory[category.id] || [];
              const isExpanded = expandedCategory === category.id;
              
              return (
                <div key={category.id} className="border border-white/10 rounded-lg overflow-hidden">
                  {/* Category Header */}
                  <button
                    type="button"
                    onClick={() => toggleCategory(category.id)}
                    className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${category.color}20` }}
                      >
                        <CategoryIcon className="w-5 h-5" style={{ color: category.color }} />
                      </div>
                      <div className="text-left">
                        <p className="text-white font-medium">{category.name}</p>
                        <p className="text-xs text-[#64748B]">{categoryGames.length} oyun</p>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-[#64748B]" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-[#64748B]" />
                    )}
                  </button>
                  
                  {/* Games List */}
                  {isExpanded && (
                    <div className="p-4 pt-0 grid grid-cols-2 md:grid-cols-3 gap-2">
                      {categoryGames.map((game) => (
                        <button
                          key={game.game_id}
                          type="button"
                          onClick={() => setSelectedGame(game.game_id)}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            selectedGame === game.game_id
                              ? "border-[#4DFFFF] bg-[#4DFFFF]/10"
                              : "border-white/10 hover:border-white/30"
                          }`}
                          data-testid={`select-game-${game.game_id}`}
                        >
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: `${game.color}33` }}
                            >
                              <CategoryIcon className="w-4 h-4" style={{ color: game.color }} />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm text-white font-medium truncate">{game.name_tr || game.name}</p>
                              <p className="text-xs text-[#64748B]">{game.min_players}-{game.max_players} kişi</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Selected Game Info */}
          {selectedGameData && (
            <div className="mt-4 p-4 bg-[#4DFFFF]/10 border border-[#4DFFFF]/30 rounded-lg">
              <p className="text-[#4DFFFF] font-medium">Seçilen: {selectedGameData.name_tr || selectedGameData.name}</p>
              <p className="text-sm text-[#B1C1C1] mt-1">{selectedGameData.description}</p>
            </div>
          )}
        </div>
        
        {/* Customizable Rules (if game selected and has rules) */}
        {selectedGameData?.customizable_rules && selectedGameData.customizable_rules.length > 0 && (
          <div className="glass-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Settings2 className="w-5 h-5 text-[#C6A87C]" />
              <Label className="text-[#C6A87C] text-sm uppercase tracking-wider">Kural Ayarları</Label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedGameData.customizable_rules.map((rule) => (
                <div key={rule.name}>
                  <Label className="text-[#B1C1C1] text-sm">{rule.label}</Label>
                  {rule.type === "select" ? (
                    <select
                      value={customRules[rule.name] ?? rule.default}
                      onChange={(e) => handleRuleChange(rule.name, e.target.value)}
                      className="w-full mt-1 p-2 bg-[#050E2E]/50 border border-white/10 rounded-lg text-white"
                    >
                      {rule.options.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : rule.type === "boolean" ? (
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="checkbox"
                        checked={customRules[rule.name] ?? rule.default}
                        onChange={(e) => handleRuleChange(rule.name, e.target.checked)}
                        className="w-5 h-5 rounded border-white/20"
                      />
                      <span className="text-sm text-[#64748B]">{customRules[rule.name] ?? rule.default ? "Açık" : "Kapalı"}</span>
                    </div>
                  ) : (
                    <Input
                      type="number"
                      value={customRules[rule.name] ?? rule.default}
                      onChange={(e) => handleRuleChange(rule.name, Number(e.target.value))}
                      min={rule.min}
                      max={rule.max}
                      className="mt-1 bg-[#050E2E]/50 border-white/10 text-white"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* League Details */}
        <div className="glass-card p-6 space-y-4">
          <div>
            <Label htmlFor="name" className="text-[#B1C1C1]">Lig Adı</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Örn: Kahve Köşesi Ligi"
              className="mt-2 bg-[#050E2E]/50 border-white/10 text-white h-12"
              required
              data-testid="league-name-input"
            />
          </div>
          
          <div>
            <Label htmlFor="description" className="text-[#B1C1C1]">Açıklama (Opsiyonel)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Lig hakkında kısa bir açıklama..."
              className="mt-2 bg-[#050E2E]/50 border-white/10 text-white"
              rows={3}
            />
          </div>
        </div>

        {/* Players */}
        <div className="glass-card p-6">
          <Label className="text-[#C6A87C] text-sm uppercase tracking-wider">Oyuncular</Label>
          
          {/* Search */}
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
            <Input
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Kullanıcı adı ara..."
              className="pl-10 bg-[#050E2E]/50 border-white/10 text-white h-12"
              data-testid="player-search-input"
            />
            {searching && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4DFFFF] animate-spin" />
            )}
            
            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-2 glass-card rounded-lg overflow-hidden">
                {searchResults.map((user) => (
                  <button
                    key={user.user_id}
                    type="button"
                    onClick={() => addPlayer(user.username)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#4DFFFF]/20 flex items-center justify-center">
                      <span className="text-sm font-bold text-[#4DFFFF]">
                        {user.name?.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-white text-sm">{user.name}</p>
                      <p className="text-xs text-[#64748B]">@{user.username}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Selected Players */}
          <div className="mt-4 space-y-2">
            {playerUsernames.filter(u => u).map((username, index) => (
              <div 
                key={index}
                className="flex items-center justify-between p-3 rounded-lg bg-white/5"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#C6A87C]/20 flex items-center justify-center">
                    <Users className="w-4 h-4 text-[#C6A87C]" />
                  </div>
                  <span className="text-white">@{username}</span>
                </div>
                <button
                  type="button"
                  onClick={() => removePlayer(index)}
                  className="p-1 hover:bg-white/10 rounded"
                >
                  <X className="w-4 h-4 text-[#64748B]" />
                </button>
              </div>
            ))}
          </div>

          {/* Manual Add */}
          <div className="mt-4 flex gap-2">
            <Input
              placeholder="Kullanıcı adı gir"
              className="bg-[#050E2E]/50 border-white/10 text-white"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (e.target.value.trim()) {
                    addPlayer(e.target.value.trim());
                    e.target.value = "";
                  }
                }
              }}
            />
            <Button
              type="button"
              variant="outline"
              className="border-white/20 text-white"
              onClick={(e) => {
                const input = e.target.previousSibling;
                if (input.value.trim()) {
                  addPlayer(input.value.trim());
                  input.value = "";
                }
              }}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          <p className="text-xs text-[#64748B] mt-2">
            En az {selectedGameData?.min_players || 2} oyuncu gerekli
          </p>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={loading || !selectedGame || !name}
          className="w-full bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90 font-['Barlow_Condensed'] uppercase tracking-wider font-bold h-12"
          data-testid="create-league-submit"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Oluşturuluyor...
            </>
          ) : (
            <>
              <Trophy className="w-4 h-4 mr-2" />
              Lig Oluştur
            </>
          )}
        </Button>
      </form>
    </div>
  );
}
